<template>
  <div class="expected-work-days-settings">
    <div
      v-if="!feature.available"
      v-html="feature.alert_html"
    />

    <div
      class="row"
      :class="{ 'section-disabled': !feature.available }"
    >
      <div class="col-md-12">
        <div class="form-cta">
          <h4>Default settings</h4>
          <AppButton
            type="primary"
            size="sm"
            :disabled="!hasDefaultFormChanges"
            @click="validate"
          >
            Save
          </AppButton>
          <AppButton
            type="text"
            size="sm"
            :disabled="!hasDefaultFormChanges"
            @click="cancel"
          >
            Cancel
          </AppButton>
        </div>
        <ExpectedWorkDaysForm
          ref="form"
          :limit-by-shifts="limitByShifts"
          :weekly-limit="weeklyLimit"
          :daily-limit="dailyLimit"
          :expected-per-week="expectedPerWeek"
          :sorted-weekdays="sortedWeekdays"
          :organization="organization"
          :dow-expected="dowExpected"
          :dow-limit="dowLimit"
          :weekly-limit-status="weeklyLimitStatus"
          :daily-limit-status="dailyLimitStatus"
          :limits-by-shifts-enabled="limitsByShiftsEnabled"
          @setWeeklyLimit="setWeeklyLimit"
          @setDailyLimit="setDailyLimit"
          @setExpectedPerWeek="setExpectedPerWeek"
          @setDowLimit="setDowLimit"
          @setDowExpected="setDowExpected"
          @setLimitsByShifts="setLimitsByShifts"
          @removeExpectedPerWeek="removeExpectedPerWeek"
          @removeRecurringWeeklyLimit="removeWeeklyLimit"
          @removeRecurringDailyLimit="removeDailyLimit"
        />
      </div>
      <div
        class="mt-20"
        :class="{ 'section-disabled': !feature.available }"
      >
        <div class="col-md-12 px-15">
          <div class="border-top mb-20"></div>
          <h4>Individual settings</h4>
          <div class="table-heading-description">
            <div>Override the organization default for specific members</div>
            <div>
              <!-- TODO: enable this when mailer implemenatation in place -->
              <AppCheckbox
                v-if="false"
                v-model="notify"
              >
                Notify changes to members
              </AppCheckbox>
            </div>
          </div>
          <AppScrollableTable :has-fixed-column="false">
            <ValidationObserver>
              <AppTable
                ref="table"
                hoverable
                :table-data="members"
              >
                <template #empty>
                  <div class="mt-20 text-center">
                    <h4 class="my-0">No members</h4>
                  </div>
                </template>
                <AppTableColumn
                  v-slot="{ row }"
                  header="Name"
                >
                  <div class="d-flex align-items-center">
                    <AppAvatar
                      :avatar="row.avatar_url"
                      size="medium"
                    ></AppAvatar>
                    <div
                      :key="row.id"
                      v-tooltip="tooltipOptions"
                      :title="row.name"
                      class="member-name"
                    >
                      {{ row.name }}
                    </div>
                  </div>
                </AppTableColumn>
                <AppTableColumn
                  v-slot="{ row }"
                  header="Weekly work days"
                  cell-classes="column-wide"
                >
                  <div class="expected-work-link">
                    <AppHoverLink
                      title="Edit member"
                      @click="openMemberPage(row.user_id)"
                    >
                      <span>Expected work days: </span>
                      <span class="days-of-work-string">
                        <b>{{ getExpectedDays(row.dow_expected) }}</b>
                      </span>
                    </AppHoverLink>
                  </div>
                  <div class="expected-work-link mt-5">
                    <AppHoverLink
                      title="Edit member"
                      @click="openMemberPage(row.user_id)"
                    >
                      <span>Not allowed to track time on: </span>
                      <span class="days-of-work-string">
                        <b>{{ filteredDowLimit(row.dow_limit) }}</b>
                      </span>
                    </AppHoverLink>
                  </div>
                  <div
                    v-if="row.limits_by_shifts"
                    class="expected-work-link mt-5"
                  >
                    <AppHoverLink
                      title="Edit shift"
                      @click="openMemberPage(row.user_id)"
                    >
                      <span
                        v-tooltip="tooltipOptions"
                        title="Settings disabled if you want to edit it go to member setting page => Some of the member's limits are based on shifts. Please visit the member's settings page to allow custom settings"
                      >
                        <b>Limited by shifts</b>
                      </span>
                    </AppHoverLink>
                  </div>
                </AppTableColumn>
                <AppTableColumn
                  v-slot="{ row }"
                  header="Expected work hours"
                  cell-classes="column-fixed"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    :ref="`expected_per_week_${row.id}`"
                  >
                    <div :class="{ 'has-error': errors.length }">
                      <div class="input-group">
                        <AppInput
                          :value="row.expected_per_week"
                          type="number"
                          @input="changeUserLimits(row, 'expected_per_week', $event)"
                          @blur="onSubmitUserLimits(row)"
                        />
                        <span class="input-group-addon">wk</span>
                      </div>
                    </div>
                  </ValidationProvider>
                </AppTableColumn>
                <AppTableColumn
                  v-slot="{ row }"
                  header="Weekly limit"
                  cell-classes="column-fixed"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    :ref="`weekly_limit_${row.id}`"
                  >
                    <div :class="{ 'has-error': errors.length }">
                      <div class="input-group">
                        <AppInput
                          :value="row.weekly_limit"
                          type="number"
                          @input="changeUserLimits(row, 'weekly_limit', $event)"
                          @blur="onSubmitUserLimits(row)"
                        />
                        <span class="input-group-addon">wk</span>
                      </div>
                    </div>
                  </ValidationProvider>
                </AppTableColumn>
                <AppTableColumn
                  v-slot="{ row }"
                  header="Daily limit"
                  cell-classes="column-fixed"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    :ref="`daily_limit_${row.id}`"
                  >
                    <div :class="{ 'has-error': errors.length }">
                      <div class="input-group">
                        <AppInput
                          :value="row.daily_limit"
                          :disabled="row.limits_by_shifts"
                          type="number"
                          @input="changeUserLimits(row, 'daily_limit', $event)"
                          @blur="onSubmitUserLimits(row)"
                        />
                        <span class="input-group-addon">day</span>
                      </div>
                    </div>
                  </ValidationProvider>
                </AppTableColumn>
              </AppTable>
            </ValidationObserver>
          </AppScrollableTable>
          <AppPagination
            v-if="showPagination"
            :pagination="pagination"
            :resource="{ one: 'member', many: 'members' }"
            hide-pagination-showing-text
            @page-changed="setPage"
          />
        </div>
      </div>
    </div>
    <AppDialog
      class="work-time-limits-override-dialog"
      :show="showOverrideDialog"
      title="Override individual settings?"
      size="smedium"
      position="centered"
      append-to-body
      @close="toggleOverrideDialog"
    >
      <p class="mb-20">
        Some members have settings with values that override the current defaults. Do you wish to reset all individual
        settings to new default values?
      </p>
      <div slot="footer">
        <div class="d-flex flex-column gap-10">
          <AppButton
            type="default"
            @click="onOverrideSettingsDialogSubmit()"
          >
            Reset all to new default values
          </AppButton>
          <AppButton
            class="ml-0"
            type="primary"
            @click="onOverrideSettingsDialogSubmit(false)"
          >
            Keep individual overriding values
          </AppButton>
          <AppCheckbox
            v-model="shouldNotifyMembers"
            class="mx-0 my-5"
          >
            Email changes to members
          </AppCheckbox>
        </div>
      </div>
    </AppDialog>
    <AppDialog
      :show="showEmailChangesDialog"
      title="Email changes to members?"
      size="smedium"
      class="work-limits-expectations-dialog"
      position="centered"
      append-to-body
      @close="toggleEmailChangesDialog"
    >
      <p class="mb-20">If you choose to notify your members, they will receive an email with the changes made.</p>
      <div slot="footer">
        <div class="d-flex flex-column gap-10">
          <AppButton
            type="default"
            @click="onEmailChangesDialogSubmit(true)"
          >
            Save changes and notify members
          </AppButton>
          <AppButton
            type="default"
            @click="onEmailChangesDialogSubmit"
          >
            Save changes but don’t notify members
          </AppButton>
        </div>
      </div>
    </AppDialog>
  </div>
</template>
<script>
import ExpectedWorkDaysForm from '~/components/members/expected_work_days/ExpectedWorkDaysForm.vue';
import AppPagination from '~/components/AppPagination.vue';
import { MAX_DAILY_LIMIT } from '~/app_constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';
import { formatDuration } from '~/utils/time_utils';
import { daysOfWork, DAYS_ALLOWED_TO_WORK } from '~/utils/string_utils';
import { OrganizationsResource } from '~/resources';

export default {
  name: 'WorkLimitsAndExpectations',
  components: {
    ExpectedWorkDaysForm,
    AppPagination,
  },
  data() {
    return {
      notify: true,
      MAX_DAILY_LIMIT,
      showOverrideDialog: false,
      showEmailChangesDialog: false,
      hasDefaultFormChanges: false,
      shouldNotifyMembers: true,
      errors: {},
      tooltipOptions: Object.freeze({ placement: 'top' }),
      initialDailyLimit: null,
      initialWeeklyLimit: null,
      initialExpectedPerWeek: null,
      initialDowExpected: null,
      initialDowLimit: null,
    };
  },
  computed: {
    ...mapState(['organization', 'members', 'pagination', 'setting_tabs']),
    ...mapGetters(['sortedWeekdays', 'currentUserIsOrganizationOwner', 'hasMembersWithCustomLimitsSettings']),
    ...mapFormFields(
      'organization',
      {
        dailyLimit: 'work_limits_and_expectations.daily_limit',
        limitByShifts: 'work_limits_and_expectations.limits_by_shifts',
        weeklyLimit: 'work_limits_and_expectations.weekly_limit',
        expectedPerWeek: 'work_limits_and_expectations.expected_per_week',
        dowExpected: 'dow_expected',
        dowLimit: 'dow_limit',
      },
      'updateField',
    ),
    showPagination() {
      return !this.loading && this.pagination?.total_pages > 1;
    },
    payRate() {
      return this.membership && this.membership.payments && this.membership.payments.pay_rate;
    },
    weeklyLimitStatus() {
      const hasPayRate = this.payRate > 0 && !this.hasFixedRate;

      if (!this.weeklyLimit) {
        return 'No limit';
      }
      if (this.currentUserIsOrganizationOwner && hasPayRate) {
        return `${this.formatLimitRate(this.weeklyLimit)} per week`;
      }

      const formattedHours = this.formatLimitDuration(this.weeklyLimit * 3600);

      return `${formattedHours} hours per week`;
    },
    limitsByShiftsEnabled() {
      return false;
    },
    dailyLimitStatus() {
      let status;
      const validLimit = this.dailyLimit <= this.MAX_DAILY_LIMIT;
      const hasPayRate = this.payRate > 0 && !this.hasFixedRate;

      if (!this.dailyLimit) {
        status = 'No limit';
      } else {
        if (validLimit) {
          if (this.currentUserIsOrganizationOwner && hasPayRate) {
            status = `${this.formatLimitRate(this.dailyLimit)} per day`;
          } else {
            const formattedHours = this.formatLimitDuration(this.dailyLimit * 3600);

            status = `${formattedHours} hours per day`;
          }
        }
      }

      return status;
    },
    feature() {
      return this.organization.feature;
    },
  },
  created() {
    if (this.feature.available) {
      this.loadData({ page: this.pagination.current_page, organizationId: this.organization.id });
    }

    this.initialDailyLimit = structuredClone(this.dailyLimit);
    this.initialWeeklyLimit = structuredClone(this.weeklyLimit);
    this.initialExpectedPerWeek = structuredClone(this.expectedPerWeek);
    this.initialDowExpected = structuredClone(this.dowExpected);
    this.initialDowLimit = structuredClone(this.dowLimit);
  },
  methods: {
    ...mapActions(['updateDowLimit', 'updateDowExpected', 'loadData', 'setUserLimits', 'submitUserLimits']),
    getExpectedDays(days) {
      return daysOfWork(days);
    },
    filteredDowLimit(days) {
      const _days = DAYS_ALLOWED_TO_WORK.filter((day) => !days.includes(day.text)).map((day) => day.text);

      return daysOfWork(_days) || '-';
    },
    formatLimitDuration(limit) {
      return formatDuration(limit, { showSeconds: 'never' });
    },
    setWeeklyLimit(val) {
      this.weeklyLimit = val;
      this.hasDefaultFormChanges = true;
    },
    setDailyLimit(val) {
      this.dailyLimit = val;
      this.hasDefaultFormChanges = true;
    },
    setExpectedPerWeek(val) {
      this.expectedPerWeek = val;
      this.hasDefaultFormChanges = true;
    },
    removeExpectedPerWeek() {
      this.expectedPerWeek = null;
      this.hasDefaultFormChanges = true;
    },
    removeDailyLimit() {
      this.dailyLimit = null;
      this.hasDefaultFormChanges = true;
    },
    removeWeeklyLimit() {
      this.weeklyLimit = null;
      this.hasDefaultFormChanges = true;
    },
    /**
     * Manages the dow_limit days
     *
     * @param {string} day - mon | tue...
     * @param {string} event remove | add
     */
    setDowLimit({ day, event }) {
      if (event === 'remove') {
        const dayIndex = this.dowLimit.indexOf(day);
        const _dowLimit = this.dowLimit.slice(0, dayIndex).concat(this.dowLimit.slice(dayIndex + 1));

        this.dowLimit = _dowLimit;
      } else {
        this.dowLimit = [...this.dowLimit, day];
      }
      this.hasDefaultFormChanges = true;
    },
    setDowExpected(val) {
      this.dowExpected = val;
      this.hasDefaultFormChanges = true;
    },
    setLimitsByShifts(val) {
      this.limitByShifts = val;
    },
    setPage(page) {
      this.loadData({ page });
    },
    toggleOverrideDialog() {
      this.showOverrideDialog = !this.showOverrideDialog;
    },
    toggleEmailChangesDialog() {
      this.showEmailChangesDialog = !this.showEmailChangesDialog;
    },
    // TODO: get this link from the BE
    openMemberPage(memberId, tab = 'work hours') {
      const url = `/organizations/${this.organization.id}/members/${memberId}`;

      window.location.href = [url, tab && `?tab=${tab}`].filter(Boolean).join('');
    },
    opensShiftsPage(tab = 'work hours') {
      const url = OrganizationsResource.shiftsLink({ id: this.organization.id });

      window.location.href = [url, tab && `?tab=${tab}`].filter(Boolean).join('');
    },
    async validate() {
      const valid = await this.$refs.form.validate();

      if (!valid) {
        return;
      }

      if (this.hasMembersWithCustomLimitsSettings) {
        this.toggleOverrideDialog();
      } else {
        this.toggleEmailChangesDialog();
      }
    },
    onOverrideSettingsDialogSubmit(override = true) {
      this.toggleOverrideDialog();
      this.submit({ override, notify: this.shouldNotifyMembers });
    },
    onEmailChangesDialogSubmit(notify = false) {
      this.toggleEmailChangesDialog();
      this.submit({ override: true, notify });
    },
    setErrors(e) {
      let _errors;

      Object.keys(e.errors[0]).forEach(async (key) => {
        _errors = e.errors[0][key];
        await this.$refs[`${key}_${e.userId}`].setErrors(_errors.join(', '));
        if (this.dirty) {
          setTimeout(() => {
            alert(_errors.join(', '));
          }, 100);
          this.dirty = false;
        }
      });
    },
    async submit({ override, notify }) {
      try {
        const data = {
          weekly_limit: this.weeklyLimit,
          daily_limit: this.dailyLimit,
          expected_per_week: this.expectedPerWeek,
          dow_limit: this.dowLimit,
          dow_expected: this.dowExpected,
          limits_by_shifts: this.limitByShifts,
          override_members_limits: override,
          notify_members: notify,
        };

        await this.$store.dispatch('setOrganizationLimits', data);

        this.$appMessage('Information saved successfully', {
          header: 'Default settings saved',
        });
        const page = this.pagination?.current_page || 1;

        this.loadData({ page });
      } catch (e) {
        this.$appMessage(e);
      }
    },
    changeUserLimits(user, key, value) {
      // We need to track somehow if the input value was changed since the last submit
      // Otherwise we get a loop: @blur => Submit => Error => Alert => Alert OK => @blur => Submit => Error
      this.dirty = true;
      this.setUserLimits({ id: user.id, key, value });
    },
    async onSubmitUserLimits(user) {
      try {
        await this.submitUserLimits(user);
      } catch (e) {
        if (e.errors && e.userId) {
          this.setErrors(e);
        }
      }
    },
    cancel() {
      this.dailyLimit = this.initialDailyLimit;
      this.weeklyLimit = this.initialWeeklyLimit;
      this.expectedPerWeek = this.initialExpectedPerWeek;
      this.dowExpected = this.initialDowExpected;
      this.dowLimit = this.initialDowLimit;
    },
  },
};
</script>
